import { Spinner, Text } from "@chakra-ui/react";
import EditPractitionerProfile from "../../../src/accounts/practitioner-account/editProfiles/EditPractitionerProfile";
import { useRouter } from "next/router";
import { IoChevronBackOutline } from "react-icons/io5";
import React, { useMemo, useCallback, useState } from "react";
import withAuth from "../../../src/hooks/withAuth";
import { useSelector, useDispatch } from "react-redux";
import { patchData } from "../../../src/utilities/axios";
import {
  getUserObject,
  updateUserDetails,
} from "../../../src/redux/actions/user";
import { resetProfileUpdates } from "../../../src/redux/actions/user";
import { motion, AnimatePresence } from "framer-motion";
import * as Yup from "yup";
import Link from "next/link";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const schemas = {
  aboutMe: Yup.string(),
  summary: Yup.string(),
  averagePrice: Yup.number("averagePrice - Must be a numerical value"),
  fullname: Yup.string().required("fullname - Full name required"),
  email: Yup.string().email().required("email - Contact email required"),
  feeStructure: Yup.string(),
  practiceNumber: Yup.string().matches(
    phoneRegExp,
    "practiceNumber - Number is not valid"
  ),
  mobileNumber: Yup.string().matches(
    phoneRegExp,
    "phoneNumber - Number is not valid"
  ),
};

const errorText = {
  aboutMe: "About me is required",
  summary: "Summary is required",
  averagePrice: "Average fee must be a numerical value",
  fullname: "Full name is required",
  email: "Contact email is required",
  feeStructure: "Fee structure is required",
  practiceNumber: "Contact number is invalid",
  mobileNumber: "Mobile number is invalid",
};

const errorAnimation = {
  x: [0, -150, 150, 0, -150, 150, 0, -150, 150, 0],
  opacity: 1,
  backgroundColor: "#E53E3E",
  color: "white",
};

const EditProfilePage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const profileUpdating = useSelector(
    (state) => state.profileEdit.updatedProfileValues
  );
  const token = useSelector((state) => state.user.JWTToken);
  const profile = useSelector(
    (state) => state?.user?.userDetails?.therapistProfile
  );
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const updating = useMemo(() => {
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }
    return Object.keys(profileUpdating).length > 0;
  }, [errors, profileUpdating]);

  const saveProfile = useCallback(async () => {
    if (submitting) {
      return;
    }

    setErrors({});
    setSubmitting(true);

    const newErrors = {};
    for (const value of Object.keys(profileUpdating)) {
      if (!schemas[value]) {
        continue;
      }

      const valid = await schemas[value].isValid(profileUpdating[value]);
      if (!valid) {
        newErrors[value] = true;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setSubmitting(false);
      setErrors(newErrors);
      return;
    }

    try {
      await patchData(
        token,
        {
          profileId: profile.profileId,
          practitionerType: "therapist",
          ...profileUpdating,
          profileComplete: profile.profileComplete,
        },
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}profile`
      );

      await dispatch(
        updateUserDetails({
          therapistProfile: {
            ...profile,
            ...profileUpdating,
          },
        })
      );
      await dispatch(getUserObject(token));
      dispatch(resetProfileUpdates());
    } catch (e) {
      console.error(e);
    }

    setSubmitting(false);
  }, [dispatch, profile, profileUpdating, submitting, token]);

  return (
    <div className="w-full h-full bg-brand-bg">
      <AnimatePresence>
        {updating && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-24 z-[10001] flex justify-center w-full flex-col items-center"
          >
            <motion.div
              transition={{ duration: 0.2 }}
              animate={Object.keys(errors).length > 0 ? errorAnimation : {}}
              className={`rounded-lg bg-white flex flex-row pl-4 pr-2 py-2 shadow items-center gap-4 ${Object.keys(errors).length > 0 ? "bg-red-200" : "bg-white"} transition`}
            >
              {Object.keys(errors).length > 0
                ? "Some settings are invalid"
                : "You have unsaved changes"}
              <button
                className={`rounded-md bg-[#587500] ${submitting ? "opacity-[0.7]" : "opacity-1 cursor-pointer"} hover:opacity-[0.7] px-4 py-2 text-white transition`}
                onClick={saveProfile}
              >
                {submitting ? <Spinner color="white" size="sm" /> : <p>Save</p>}
              </button>
            </motion.div>
            {Object.keys(errors).length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-2 px-4 bg-[#E53E3E] mt-2 rounded-lg flex flex-col shadow items-center gap-2 divide-y"
              >
                {Object.keys(errors).map((item) => (
                  <div className="w-full text-white text-center" key={item}>
                    {errorText[item]}
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="bg-brand-alt flex justify-between items-end w-full">
        <div className="max-w-[1148px] py-4 sm:py-0 w-full mx-auto flex justify-between items-start md:items-center px-6 flex-col md:flex-row">
          <div className="flex flex-col py-2 md:py-8">
            <Link
              href={{ pathname: "/accounts/practitioner-account/dashboard" }}
              className="w-min block"
            >
              <div className="text-white cursor-pointer text-sm flex opacity-70 items-center p-2 pb-0 pl-0 flex-0 flex-shrink w-min">
                <IoChevronBackOutline />
                <span>Back</span>
              </div>
            </Link>
            <Text fontSize="3xl" color="white" className="font-bold">
              Profile settings
            </Text>
          </div>
        </div>
      </div>
      <EditPractitionerProfile errors={errors} practitionerType="therapist" />
    </div>
  );
};

export default withAuth(EditProfilePage);
